// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { identity } from 'lodash';
import { createSelector } from 'reselect';
import { branch } from '../../../node_module_clones/recompose';

// Utils
import dontUpdateForKeys from '../../utils/milanoteRecompose/dontUpdateForKeys';
import { gridSizeSelector } from '../../utils/grid/gridSizeSelector';
import { getElementId, getIsCollapsed, getThreadId } from '../../../common/elements/utils/elementPropertyUtils';
import { isLocationAttached } from '../../../common/elements/utils/elementLocationUtils';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import {
    getIsPresentationModeEditingDisabled,
    getIsPresentationModeHideCommentsEnabled,
} from '../../workspace/presentation/presentationSelector';

import elementLineEdgeDropTarget from '../line/elementLineEdgeDropTarget';

// Actions
import { commentsAdd } from './store/commentActions';
import { moveElementsToTrash } from '../actions/elementShortcutActions';

// Components
import CommentThread from './CommentThread';

const mapDispatchToProps = (dispatch) => ({
    dispatchMoveElementsToTrash: ({ currentBoardId, elementIds, transactionId }) =>
        dispatch(moveElementsToTrash({ currentBoardId, elementIds, transactionId })),
    dispatchCommentsAdd: ({ elementId, threadId, text, transactionId }) =>
        dispatch(commentsAdd({ elementId, threadId, text, transactionId })),
});

const mapStateToProps = createSelector(
    gridSizeSelector,
    getIsPresentationModeHideCommentsEnabled,
    getIsPresentationModeEditingDisabled,
    ({ gridSize }, isHideCommentsEnabled, isEditingDisabled) => ({
        gridSize,
        isHideCommentsEnabled,
        isEditingDisabled,
    }),
);

@connect(mapStateToProps, mapDispatchToProps)
@branch(({ element }) => getIsCollapsed(element) || isLocationAttached(element), identity, elementLineEdgeDropTarget)
@dontUpdateForKeys(['dragModifierKeys'])
class CommentThreadContainer extends React.Component {
    moveElementsToTrash = () => {
        const { currentBoardId, element, dispatchMoveElementsToTrash, onMoveToTrash } = this.props;
        const transactionId = getNewTransactionId();
        dispatchMoveElementsToTrash({ currentBoardId, elementIds: [getElementId(element)], transactionId });
        onMoveToTrash?.({ transactionId });
    };

    addComment = (text, transactionId) => {
        const { dispatchCommentsAdd, element } = this.props;
        const threadId = getThreadId(element);
        const elementId = getElementId(element);
        dispatchCommentsAdd({ elementId, threadId, text, transactionId });
    };

    startEditing = ({ editorId, editorKey, editorFocusRect }) => {
        const { element, isEditable, dispatchStartEditingElement } = this.props;

        // The comment thread can be edited even when it's remotely selected
        if (!isEditable) return;

        dispatchStartEditingElement({ id: getElementId(element), editorId, editorKey, editorFocusRect });
    };

    render() {
        const {
            connectLineEdgeDropTarget,
            canConnectLineEdge,
            element,
            isHideCommentsEnabled,
            isPresentationModeEnabled,
            isEditingDisabled,
        } = this.props;

        const isCollapsed = getIsCollapsed(element) || isLocationAttached(element);

        const lineEdgeConnector =
            !isCollapsed && connectLineEdgeDropTarget && canConnectLineEdge ? connectLineEdgeDropTarget : identity;

        const hideComments = isPresentationModeEnabled && isHideCommentsEnabled;
        const hideEditing = isPresentationModeEnabled && isEditingDisabled;

        return lineEdgeConnector(
            <div>
                <CommentThread
                    {...this.props}
                    moveElementsToTrash={this.moveElementsToTrash}
                    startEditing={this.startEditing}
                    addComment={this.addComment}
                    hideComments={hideComments}
                    hideEditing={hideEditing}
                />
            </div>,
        );
    }
}

CommentThreadContainer.propTypes = {
    element: PropTypes.object.isRequired,
    currentBoardId: PropTypes.string.isRequired,
    createCard: PropTypes.func,
    inList: PropTypes.string,
    gridSize: PropTypes.number,
    connectDropTarget: PropTypes.func,
    dispatchMoveElementsToTrash: PropTypes.func,
    dispatchCommentsAdd: PropTypes.func,
    isEditable: PropTypes.bool,
    isHideCommentsEnabled: PropTypes.bool,
    isEditingDisabled: PropTypes.bool,
    isPresentationModeEnabled: PropTypes.bool,
    connectLineEdgeDropTarget: PropTypes.func,
    dispatchStartEditingElement: PropTypes.func,
    canConnectLineEdge: PropTypes.bool,
    onMoveToTrash: PropTypes.func,
};

export default CommentThreadContainer;
